import moment, {type Moment} from 'moment-timezone'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  string,
  number,
  record,
  array,
  nullable,
  oneOf,
  date,
  mapStatic
} from '@freckle/parser'
import type {TeacherRole} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import {TeacherRoles} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import type {
  PremiumCutoff,
  PremiumLicensesAttrs
} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {parser as identityProviderParser} from '@freckle/educator-entities/ts/roster/identity-provider'

export type SchoolSimplifiedT = {
  id: number
  name: string
}

export type StudentSimplifiedT = {
  id: number
  firstName: string
  lastName: string
}

export type TeacherTableRowT = {
  id: number
  firstName: string
  lastName: string
  email: string
  grades: Array<number>
  school: SchoolSimplifiedT
  students: Array<StudentSimplifiedT>
  role: TeacherRole | undefined | null
  source: IdentityProviderT | undefined | null
  premiumLicenses: PremiumLicensesAttrs | undefined | null
}

function parse(response: unknown): TeacherTableRowT {
  const schoolSimplifiedAttrs = record({
    id: number(),
    name: string()
  }) as ParserT<SchoolSimplifiedT>

  const studentSimplifiedAttrs = record({
    id: number(),
    firstName: string(),
    lastName: string()
  }) as ParserT<StudentSimplifiedT>

  const attrs = record({
    id: number(),
    firstName: string(),
    lastName: string(),
    email: string(),
    grades: array(number()),
    school: schoolSimplifiedAttrs,
    students: array(studentSimplifiedAttrs),
    role: nullable(oneOf('TeacherRole', TeacherRoles.all())),
    source: nullable(identityProviderParser),
    premiumLicenses: nullable(parsePremiumLicensesAttrs)
  }) as ParserT<TeacherTableRowT>
  return Parser.run(response, attrs)
}

export function parseTeacherTableRows(response: Array<unknown>): Array<TeacherTableRowT> {
  return response.map(parse)
}

const makePremiumCutoff = (m: Moment): PremiumCutoff => m

const parsePremiumLicensesAttrs: ParserT<PremiumLicensesAttrs> = record({
  mathPremiumUntil: nullable(mapStatic(date(), moment)),
  elaPremiumUntil: nullable(mapStatic(date(), moment)),
  socialStudiesPremiumUntil: nullable(mapStatic(date(), moment)),
  sciencePremiumUntil: nullable(mapStatic(date(), moment)),
  premiumCutoff: mapStatic(mapStatic(date(), moment), makePremiumCutoff)
})
